/**
 * Stepping the playhead along the Step grid. The grid is every whole multiple of the step, from beat 0.
 * A playhead that is already on it moves one step; one that is between two lines (it was parked there
 * before the step changed, or by a click on the timeline) moves to the next line in the direction it
 * is going, so a step never lands between valid beats.
 */
import { isSynthVrView } from './editorSettings';

/** How close to a grid line, as a fraction of a step, still counts as on it. Triplet steps (1/3, 1/6)
 *  are not exact in floating point, so a playhead placed by an earlier step is a hair off its line. */
const ON_GRID = 1e-6;

/** The beat one step from `beat` in direction `dir` (+1 forward, -1 back), on the grid of `step`.
 *  Returns `beat` unchanged when `step` is not a positive number. */
export function steppedBeat(beat: number, step: number, dir: 1 | -1): number {
  if (!(step > 0)) return beat;
  const lines = beat / step;
  const nearest = Math.round(lines);
  const onLine = Math.abs(lines - nearest) < ON_GRID;
  const target = onLine ? nearest + dir : dir > 0 ? Math.ceil(lines) : Math.floor(lines);
  return target * step;
}

/** The beat a step or a wheel notch lands on in `view`: the Step grid in Synth VR, a flat `step` from
 *  wherever the playhead is in every other view, which keep the old behaviour. */
export function stepFrom(view: string | undefined, beat: number, step: number, dir: 1 | -1): number {
  return isSynthVrView(view) ? steppedBeat(beat, step, dir) : beat + dir * step;
}
